import styled from 'styled-components';

// Full-width content with comfortable padding (no narrow max-width cap) so wide
// tables / diffs read well. Visual language kept close to Redoc's content pane.
export default styled.div`
    padding: 32px 40px 80px;
    color: #333;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    line-height: 1.6;

    h1.changelog-title {
        font-size: 28px;
        font-weight: 600;
        margin: 0 0 24px;
    }

    .changelog-empty {
        font-size: 15px;
        color: #8c8c8c;
        margin: 0;
    }

    .changelog-entry {
        padding: 24px 0;
        border-top: 1px solid #ededed;
    }

    .changelog-entry:first-of-type {
        border-top: none;
        padding-top: 0;
    }

    /* Prominent per-entry title (the release date) — the versioning/scan anchor.
       Sits above the quiet Staging/Production metadata. */
    .changelog-entry-title {
        font-size: 20px;
        font-weight: 600;
        color: #1d1d1d;
        margin: 0 0 6px;
    }

    /* One update (module/topic) within an entry; a dashed rule separates
       consecutive updates under the same date. */
    .changelog-update + .changelog-update {
        margin-top: 20px;
        padding-top: 16px;
        border-top: 1px dashed #e0e0e0;
    }

    /* Optional subtitle naming what the release is about, beneath the date. */
    .changelog-entry-subtitle {
        font-size: 16px;
        font-weight: 600;
        margin: 0 0 4px;
    }

    /* Optional one-line summary beneath the subtitle. */
    .changelog-entry-description {
        font-size: 14px;
        margin: 0 0 10px;
    }

    /* At-a-glance signal: a red pill next to the date when the entry has any
       breaking changes, so readers can spot risky releases without scanning. */
    .changelog-breaking-badge {
        display: inline-block;
        margin-left: 12px;
        padding: 2px 10px;
        border-radius: 4px;
        background: #c62828;
        color: #fff;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.3px;
        text-transform: uppercase;
        vertical-align: middle;
    }

    /* Breaking vs normal blocks within an entry. The breaking block leads with a
       red heading + left rule; the normal block flows plainly beneath it. */
    .changelog-section {
        margin: 16px 0 0;
    }

    .changelog-section-title {
        font-size: 15px;
        font-weight: 600;
        margin: 0 0 4px;
    }

    /* Action needed: what (if anything) consumers must do — blue left rule to
       stand apart from the red breaking block. */
    .changelog-section--action {
        border-left: 3px solid #1565c0;
        padding-left: 14px;
    }

    .changelog-section--action .changelog-section-title {
        color: #1565c0;
    }

    /* Later update to an already-published entry — bright orange (red is breaking, yellow is deprecated)
       so post-release notes stand out. */
    .changelog-section--update {
        border-left: 3px solid #f57c00;
        padding-left: 14px;
    }

    .changelog-section--update .changelog-section-title {
        color: #f57c00;
    }

    .changelog-section--breaking {
        border-left: 3px solid #c62828;
        padding-left: 14px;
    }

    .changelog-section--breaking .changelog-section-title {
        color: #c62828;
    }

    /* Dual environment header: outlined Staging / Production chips + their dates.
       Quiet metadata beneath the title; outlined chips contrast with filled badges. */
    .changelog-envs {
        display: flex;
        flex-wrap: wrap;
        gap: 8px 24px;
        align-items: baseline;
        margin: 0 0 16px;
    }

    .changelog-env {
        display: inline-flex;
        align-items: baseline;
        gap: 8px;
    }

    /* Un-boxed, quiet metadata labels — deliberately lighter than the boxed
       category badges so the hierarchy reads: badges > env metadata. */
    .env-label {
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.5px;
        text-transform: uppercase;
    }

    .env-label.staging {
        color: #a8842c;
    }

    .env-label.production {
        color: #4a8a52;
    }

    .env-date {
        font-size: 14px;
        font-weight: 500;
        color: #595959;
    }

    .env-date.env-pending {
        font-size: 14px;
        font-weight: 500;
        font-style: italic;
        color: #8c8c8c;
    }

    .changelog-category {
        margin: 16px 0 8px;
    }

    .changelog-badge {
        display: inline-block;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.3px;
        text-transform: uppercase;
        padding: 2px 10px;
        border-radius: 4px;
        color: #fff;
    }

    .changelog-badge.added       { background: #2e7d32; }
    .changelog-badge.changed     { background: #1565c0; }
    .changelog-badge.deprecated  { background: #b08800; }
    .changelog-badge.removed     { background: #c62828; }
    .changelog-badge.fixed       { background: #616161; }
    .changelog-badge.security    { background: #6a1b9a; }

    /* Each change is normal flowing content, not a bullet. */
    .changelog-body {
        margin: 8px 0 0;
    }

    .changelog-line {
        margin: 6px 0;
    }

    /* Opt-in bullets: explicit points, indented beneath the normal content. */
    .changelog-list {
        margin: 6px 0;
        padding-left: 22px;
    }

    .changelog-list > li {
        margin: 4px 0;
    }

    /* Labeled sub-section: a quiet uppercase heading above its own bullet list.
       Lighter than category badges so the hierarchy reads: badge > group label. */
    .changelog-group {
        margin: 10px 0;
    }

    .changelog-group-label {
        display: block;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        color: #595959;
        margin: 0 0 2px;
    }

    .changelog-group .changelog-list {
        margin-top: 2px;
    }

    .changelog-diff {
        margin: 6px 0;
        font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
        font-size: 13px;
    }

    .changelog-diff .field {
        font-weight: 600;
        margin-right: 6px;
    }

    .changelog-diff .from,
    .changelog-diff .to {
        background: #f4f4f4;
        border-radius: 3px;
        padding: 1px 6px;
    }

    .changelog-diff .from { color: #c62828; }
    .changelog-diff .to { color: #2e7d32; }
    .changelog-diff .arrow { margin: 0 8px; color: #888; }

    table.changelog-table {
        border-collapse: collapse;
        margin: 10px 0;
        font-size: 14px;
        background: #ffffff;
    }

    table.changelog-table th,
    table.changelog-table td {
        border: 1px solid #e0e0e0;
        padding: 6px 12px;
        text-align: left;
    }

    table.changelog-table th {
        background: #f7f7f7;
        font-weight: 600;
    }

    a {
        color: #1565c0;
        text-decoration: none;
    }

    a:hover {
        text-decoration: underline;
    }

    code {
        font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
        font-size: 0.9em;
        background: #f4f4f4;
        border-radius: 3px;
        padding: 1px 5px;
    }
`;
